// box — default skin module
// Token disposition: pass-through (no overrides)
// NO external imports — plain JavaScript only

export const styles = `
  :host {
    display: block;
  }

  [part="box"] {
    display: var(--run-box-display, block);
    overflow: var(--run-box-overflow, visible);
    position: var(--run-box-position, static);
    background: var(--run-box-background, transparent);
    box-shadow: var(--run-box-shadow, none);
    border-radius: var(--run-box-border-radius, 0);
    padding-block: var(--run-box-padding-y, var(--run-box-padding, 0));
    padding-inline: var(--run-box-padding-x, var(--run-box-padding, 0));
    box-sizing: border-box;
  }

  [part="box"][data-border] {
    border-style: solid;
    border-color: var(--run-box-border-color, currentColor);
    border-width: var(--run-box-border-width, 1px);
  }
`;

export function render(host) {
  const {
    surface,
    border,
    borderWidth,
    radius,
    elevation,
    padding,
    paddingX,
    paddingY,
    display,
    overflow,
    position,
    width,
    height,
    minWidth,
    minHeight,
    maxWidth,
    maxHeight,
    as,
    shadowRoot,
  } = host;

  const hasBorder = border != null && border !== 'none';

  // Dynamic token construction is constrained to contracted suffixes (RUN-500
  // §5): an undefined or out-of-range prop must never build an uncontracted
  // (or `-undefined`) token name. Every mapped value is a member of
  // @runsnative/token-contract; unknown inputs fall back to a contracted
  // default (or a literal for "no token"). `2xl` padding clamps to the largest
  // contracted step (`xl`) — `--run-space-semantic-2xl` is not contracted/emitted.
  const SPACE_SUFFIX     = { '2xs': '2xs', xs: 'xs', sm: 'sm', md: 'md', lg: 'lg', xl: 'xl', '2xl': 'xl' };
  const SURFACE_SUFFIX   = { default: 'default', subtle: 'subtle', raised: 'raised', inverse: 'inverse', overlay: 'overlay' };
  const ELEVATION_SUFFIX = { raised: 'raised', overlay: 'overlay', modal: 'modal' };
  const RADIUS_SUFFIX    = { control: 'semantic-control', card: 'semantic-card', pill: 'semantic-pill', checkbox: 'semantic-checkbox', badge: 'semantic-badge' };
  const BORDER_SUFFIX    = { default: 'default', strong: 'strong', subtle: 'subtle' };

  const spaceToken = (v) =>
    SPACE_SUFFIX[v] ? `var(--run-space-semantic-${SPACE_SUFFIX[v]})` : '0';

  const paddingInlineToken = spaceToken(paddingX !== null ? paddingX : padding);
  const paddingBlockToken  = spaceToken(paddingY !== null ? paddingY : padding);

  const backgroundToken = (surface && surface !== 'none' && SURFACE_SUFFIX[surface])
    ? `var(--run-color-surface-${SURFACE_SUFFIX[surface]})`
    : 'transparent';

  const shadowToken = (elevation && elevation !== 'none' && ELEVATION_SUFFIX[elevation])
    ? `var(--run-elevation-semantic-${ELEVATION_SUFFIX[elevation]})`
    : 'none';

  const radiusToken = (radius && radius !== 'none' && RADIUS_SUFFIX[radius])
    ? `var(--run-radius-${RADIUS_SUFFIX[radius]})`
    : '0';

  const borderColorToken = hasBorder
    ? `var(--run-color-border-${BORDER_SUFFIX[border] || 'default'})`
    : '';

  const borderWidthToken = hasBorder
    ? ({ hairline: '1px', thin: '1px', default: '1px', medium: '2px', thick: '2px', strong: '2px' }[borderWidth] || '1px')
    : '';

  const widthStyle = width !== null ? `width: ${width};` : '';
  const heightStyle = height !== null ? `height: ${height};` : '';
  const minWidthStyle = minWidth !== null ? `min-width: ${minWidth};` : '';
  const minHeightStyle = minHeight !== null ? `min-height: ${minHeight};` : '';
  const maxWidthStyle = maxWidth !== null ? `max-width: ${maxWidth};` : '';
  const maxHeightStyle = maxHeight !== null ? `max-height: ${maxHeight};` : '';

  const borderAttr = hasBorder ? ' data-border=""' : '';

  shadowRoot.innerHTML = `
    <${as}
      part="box"
      ${borderAttr}
      style="
        --run-box-display: ${display};
        --run-box-overflow: ${overflow};
        --run-box-position: ${position};
        --run-box-background: var(--run-box-background, ${backgroundToken});
        --run-box-shadow: var(--run-box-shadow, ${shadowToken});
        --run-box-border-radius: var(--run-box-border-radius, ${radiusToken});
        --run-box-padding-inline: var(--run-box-padding-x, ${paddingInlineToken});
        --run-box-padding-block: var(--run-box-padding-y, ${paddingBlockToken});
        ${hasBorder ? `--run-box-border-color: var(--run-box-border-color, ${borderColorToken});` : ''}
        ${hasBorder ? `--run-box-border-width: var(--run-box-border-width, ${borderWidthToken});` : ''}
        display: ${display};
        overflow: ${overflow};
        position: ${position};
        background: var(--run-box-background, ${backgroundToken});
        box-shadow: var(--run-box-shadow, ${shadowToken});
        border-radius: var(--run-box-border-radius, ${radiusToken});
        padding-inline: var(--run-box-padding-x, ${paddingInlineToken});
        padding-block: var(--run-box-padding-y, ${paddingBlockToken});
        ${hasBorder ? `border-style: solid; border-color: var(--run-box-border-color, ${borderColorToken}); border-width: var(--run-box-border-width, ${borderWidthToken});` : ''}
        ${widthStyle}
        ${heightStyle}
        ${minWidthStyle}
        ${minHeightStyle}
        ${maxWidthStyle}
        ${maxHeightStyle}
        box-sizing: border-box;
      "
    ><slot></slot></${as}>
  `;
}